import {
  CheckSquareOffset,
  MapPin,
  Package,
  Pencil,
  Plus,
  UserCircle,
} from '@phosphor-icons/react';
import { Flex, Tag, Typography, theme as antTheme } from 'antd';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { ActionEditDots, ActionMenuItem, HeaderBackButton } from '../../../components';
import { ColoredPill, StyledButton } from '../../../elements';
import { CommonUtility, PackagingStatus, theme } from '../../../utility';

const { useToken } = antTheme;

function getStatusTagColor(status) {
  if (status === PackagingStatus.inTransit) return 'default';
  if (status === PackagingStatus.readyToPack) return 'warning';
  return 'default';
}

const ExpandingText = styled.span`
  max-width: 28vw;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatusTag = styled(Tag)`
  display: inline-flex;
  align-items: center;
  margin: 0;
  line-height: 1;
`;

const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
`;

function formatStatus(status) {
  if (!status) return '';
  return CommonUtility.removeUnderscoreAndCapitalize(status);
}

function getHeaderTitle(data) {
  return (
    data?.order?.marketplace_order_display_id ||
    data?.waves?.[0]?.waveId?.waveNumber ||
    data?.toteNumber ||
    data?.palletNumber ||
    ''
  );
}

/** Full AppHeader left row: back, container id, status. */
export function PackingDetailHeaderLead({ data, backOnly = false }) {
  const { token } = useToken();
  const title = getHeaderTitle(data);
  const statusLabel = formatStatus(data?.status);
  const hasOrder = CommonUtility.isValidObject(data?.order);
  const [hover, setHover] = useState(false);

  return (
    <Flex
      align="center"
      gap={12}
      wrap="nowrap"
      style={{ minWidth: 0, maxWidth: '100%', height: '100%' }}
    >
      <HeaderBackButton />
      {!backOnly && (title || statusLabel || hasOrder) ? (
        <Flex align="center" gap={8} style={{ minWidth: 0 }} wrap="nowrap">
          {title ? (
            <Flex align="center" gap={6} style={{ minWidth: 0 }}>
              <IconWrap>
                <Package size={18} color={token.colorText} />
              </IconWrap>
              <Typography.Text
                strong
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography.Text>
            </Flex>
          ) : null}

          {hasOrder && (
            <>
              <ColoredPill mini color={theme.colors.grey}>
                <UserCircle size={16} />
                {data?.order?.shipping_address?.name}
              </ColoredPill>
              <ColoredPill
                mini
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                color={hover ? theme.colors.primary : theme.colors.grey}
              >
                <MapPin size={16} />
                <ExpandingText>
                  {hover
                    ? CommonUtility.getLongAddress(
                        data?.order?.shipping_address || {},
                        false,
                      )
                    : CommonUtility.getShortAddress(
                        data?.order?.shipping_address || {},
                        false,
                      )}
                </ExpandingText>
              </ColoredPill>
            </>
          )}

          {!hover && statusLabel ? (
            <StatusTag color={getStatusTagColor(data?.status)}>{statusLabel}</StatusTag>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
}

/** AppHeader right-side actions for packing detail. */
export function PackingDetailHeaderActions({
  addPackage,
  fulfillTote,
  isPallet,
  shipped,
  showActions = true,
  packages,
  allPacked,
  editAddress,
}) {
  const allPackagesHaveLabels = useMemo(() => {
    if (!CommonUtility.isValidArray(packages)) return false;
    return packages.every(
      pack => CommonUtility.isValidObject(pack.label) || !pack.items.length,
    );
  }, [packages]);

  if (isPallet || !showActions) return null;

  return (
    <Flex align="center" gap={8}>
      {!shipped && (
        <StyledButton
          onClick={fulfillTote}
          disabled={!allPackagesHaveLabels || !allPacked}
          className="flex-btn"
        >
          <CheckSquareOffset size={18} className="mr-1" />
          Fulfill
        </StyledButton>
      )}
      <ActionEditDots
        showByDefault
        inline
        basic
        preventChangingBg
        on="hover"
        position="bottom left"
      >
        <ActionMenuItem onClick={addPackage}>
          <Plus />
          Package
        </ActionMenuItem>
        <ActionMenuItem onClick={editAddress}>
          <Pencil />
          Change Address
        </ActionMenuItem>
      </ActionEditDots>
      {shipped ? (
        <ColoredPill mini color={theme.colors.green}>
          Shipped
        </ColoredPill>
      ) : null}
    </Flex>
  );
}
